import { useCallback, useEffect, useState } from "react";
import TableColum from "../../../models/tableColumn";
import DropdownOption from "../../common/DropdownOption";
import MapLayer from "../../../models/mapLayer";
import ThematicMapLayerRange from "../../../models/thematicMapLayerRange";
import TableColumnDataType from "../../../models/tableColumnDataType";
import ThematicMapLayerRangeType from "../../../models/thematicMapLayerRangeType";
import ProjectInfo from "../../../models/projectInfo";
import Dropdown from "../../common/Dropdown";
import unitConverter from "../../../util/units/unitConverter";
import ThematicRangesEditor from "./ThematicRangesEditor";
import ThematicValuesEditor from "./ThematicValuesEditor";

const ThematicMapLayerEdit: React.FC<{
  project: ProjectInfo;
  tableColumns: TableColum[];
  mapLayer: MapLayer;
  onSetMapLayer: any;
}> = ({ project, tableColumns, mapLayer, onSetMapLayer }) => {
  const [fieldOptions, setFieldOptions] = useState<
    DropdownOption<TableColum>[]
  >([]);
  const [selectedFieldOption, setSelectedFieldOption] =
    useState<DropdownOption<TableColum>>();

  const setNewThematicLayer = useCallback(
    (
      mapLayer: MapLayer,
      tableColumn: TableColum,
      thematicMapLayerRanges: ThematicMapLayerRange[],
    ) => {
      if (!mapLayer.thematicMapLayer) {
        return;
      }
      const newMapLayer = { ...mapLayer };
      newMapLayer.thematicMapLayer = { ...mapLayer.thematicMapLayer };
      newMapLayer.thematicMapLayer.fieldName = tableColumn.columnName;

      if (isDataTypeContinous(tableColumn.dataType)) {
        newMapLayer.thematicMapLayer.rangeType =
          ThematicMapLayerRangeType.Continuous;
      } else if (isDataTypeDiscrete(tableColumn.dataType)) {
        newMapLayer.thematicMapLayer.rangeType =
          ThematicMapLayerRangeType.Discrete;
      }

      newMapLayer.thematicMapLayer.thematicMapLayerRanges =
        thematicMapLayerRanges;

      onSetMapLayer(newMapLayer);
    },
    [onSetMapLayer],
  );

  const setFieldOptionsFromMapLayer = useCallback(
    (
      mapLayer: MapLayer,
      tableColumns: TableColum[],
      fieldOptions: DropdownOption<TableColum>[],
    ) => {
      const layerFieldName = mapLayer.thematicMapLayer?.fieldName;

      const layerTableColumn = tableColumns.find(
        (c) => c.columnName === layerFieldName,
      );

      if (layerTableColumn) {
        const layerFieldOption = {
          value: layerTableColumn,
          label: layerTableColumn.displayName,
        };

        setSelectedFieldOption(layerFieldOption);
      } else {
        const selectedFieldOption = fieldOptions[0];
        setSelectedFieldOption(selectedFieldOption);

        if (selectedFieldOption.value) {
          setNewThematicLayer(mapLayer, selectedFieldOption.value, []);
        }
      }
    },
    [setNewThematicLayer],
  );

  useEffect(() => {
    const options: DropdownOption<TableColum>[] = tableColumns.map((c) => {
      return { value: c, label: c.displayName };
    });

    setFieldOptions(options);

    setFieldOptionsFromMapLayer(mapLayer, tableColumns, options);
  }, [project, tableColumns, mapLayer, setFieldOptionsFromMapLayer]);

  const isDataTypeContinous = (dataType: TableColumnDataType) => {
    return dataType === TableColumnDataType.Number;
  };

  const isDataTypeDiscrete = (dataType: TableColumnDataType) => {
    return (
      dataType === TableColumnDataType.String ||
      dataType === TableColumnDataType.Boolean
    );
  };

  const onThematicMapLayerRangesChanged = (
    thematicMapLayerRanges: ThematicMapLayerRange[],
  ) => {
    if (!mapLayer.thematicMapLayer) {
      return;
    }

    if (!selectedFieldOption || !selectedFieldOption.value) {
      return;
    }

    setNewThematicLayer(
      mapLayer,
      selectedFieldOption.value,
      thematicMapLayerRanges,
    );
  };

  const onFieldOptionChanged = async (newValue: DropdownOption<TableColum>) => {
    setSelectedFieldOption(newValue);

    if (!newValue.value) {
      return;
    }

    setNewThematicLayer(mapLayer, newValue.value, []);
  };

  const onUseAreaChanged = (event: any) => {
    const checked = event.target.checked;

    if (!mapLayer.thematicMapLayer) {
      return;
    }

    const newMapLayer = { ...mapLayer };
    newMapLayer.thematicMapLayer = {
      ...mapLayer.thematicMapLayer,
      useArea: checked,
    };
    onSetMapLayer(newMapLayer);
  };

  return (
    <>
      <label htmlFor="field" className="form-label">
        Field
      </label>
      <div className="d-flex mb-3 justify-content-between">
        <div className="d-flex align-items-center">
          <Dropdown
            minWidth="200px"
            height="35px"
            options={fieldOptions}
            value={selectedFieldOption}
            onChange={(newValue: any) => onFieldOptionChanged(newValue)}
            disabled={mapLayer.parentMapLayer}
          />
          {selectedFieldOption && (
            <div className="ms-2">
              {selectedFieldOption.value &&
                unitConverter.getSymbol(selectedFieldOption.value.unitMeasure)}
            </div>
          )}
        </div>
        {project.isEsaSync && (
          <div className="d-flex align-items-center">
            <label className="form-check-label" htmlFor="useArea">
              Use Area
            </label>
            <div className="form-switch form-switch-right-text">
              <input
                className="form-check-input form-switch-right-text"
                type="checkbox"
                checked={mapLayer.thematicMapLayer?.useArea}
                onChange={onUseAreaChanged}
                id="useArea"
              />
            </div>
          </div>
        )}
      </div>
      {selectedFieldOption &&
        selectedFieldOption.value &&
        isDataTypeContinous(selectedFieldOption.value.dataType) && (
          <ThematicRangesEditor
            tableColumn={selectedFieldOption.value}
            thematicMapLayerRanges={
              mapLayer.thematicMapLayer?.thematicMapLayerRanges ?? []
            }
            onThematicMapLayerRangesChanged={onThematicMapLayerRangesChanged}
          />
        )}
      {selectedFieldOption &&
        selectedFieldOption.value &&
        isDataTypeDiscrete(selectedFieldOption.value.dataType) && (
          <ThematicValuesEditor
            project={project}
            tableColumn={selectedFieldOption.value}
            thematicMapLayerRanges={
              mapLayer.thematicMapLayer?.thematicMapLayerRanges ?? []
            }
            onThematicMapLayerRangesChanged={onThematicMapLayerRangesChanged}
          />
        )}
    </>
  );
};

export default ThematicMapLayerEdit;
